import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { resetWarehouseScopedCache } from '@/lib/query-client';

/**
 * Global business-channel view filter (v2). Channels are now DATA (a `channels`
 * table); the selected value is a channel **slug** string, or the `ALL`
 * sentinel for the unfiltered default. The header dropdown resolves the slug to
 * a channel row for its label + accent; unknown slugs reconcile back to `ALL`.
 */
export type ChannelSelection = 'ALL' | string;

/**
 * Legacy v1 persisted values (the hardcoded 2-value enum) → seeded slugs. Old
 * bundles persisted `RETAIL` / `MULTI_FAMILY` uppercase; migrate them on
 * rehydrate so a returning user keeps an equivalent selection.
 */
const LEGACY_SLUG_MAP: Record<string, string> = {
  RETAIL: 'retail',
  MULTI_FAMILY: 'multi-family',
};

interface ChannelState {
  selectedChannel: ChannelSelection;
  setChannel: (next: ChannelSelection) => void;
}

export const useChannelStore = create<ChannelState>()(
  persist(
    (set, get) => ({
      selectedChannel: 'ALL',

      setChannel: (next) => {
        // No-op re-select guard: the switcher fires even when the current
        // value is clicked again. Same selection → same context → clearing
        // the cache here would strand mounted observers.
        if (get().selectedChannel === next) return;

        // Reset BEFORE set(): switching channel invalidates all queries so
        // every page refetches against a clean cache (mirrors the warehouse
        // switcher, D6).
        resetWarehouseScopedCache();
        set({ selectedChannel: next });
      },
    }),
    {
      name: 'wms-channel',
      // Bump from the unversioned v1 store so `migrate` runs once for returning
      // users, mapping the legacy uppercase enum values to the seeded slugs.
      version: 1,
      migrate: (persisted, version) => {
        const state = persisted as Partial<ChannelState> | undefined;
        const selected = state?.selectedChannel;
        if (version < 1 && typeof selected === 'string') {
          const mapped = LEGACY_SLUG_MAP[selected];
          if (mapped) {
            return { ...state, selectedChannel: mapped } as ChannelState;
          }
        }
        return state as ChannelState;
      },
      partialize: (state) => ({
        selectedChannel: state.selectedChannel,
      }),
    },
  ),
);
